/**
 * Section 7.1: Session Timeout Handler
 * 
 * Monitors session activity and displays timeout warning
 * when approaching idle timeout (default: 20 minutes).
 * 
 * The backend is the authority for session expiration,
 * so this is primarily a UX enhancement.
 */

import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/auth";
import { auth } from "@/api";
import { useNavigate } from "react-router-dom";

// Configuration (should match backend defaults)
const SESSION_IDLE_TIMEOUT = 1200;  // 20 minutes
const WARNING_BEFORE = 120;  // Show warning 2 minutes before expiry
const CHECK_INTERVAL = 10000;  // Check every 10 seconds

export function useSessionTimeout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const timeoutCheckRef = useRef(null);

  // Update last activity on user interaction
  const recordActivity = () => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
  };

  // Set up activity listeners
  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      document.addEventListener(event, recordActivity);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, recordActivity);
      });
    };
  }, [user]);

  // Check session timeout periodically
  useEffect(() => {
    if (!user) return;

    const checkTimeout = () => {
      const now = Date.now();
      const lastActivity = lastActivityRef.current;
      const elapsedSeconds = Math.floor((now - lastActivity) / 1000);
      const secondsLeft = SESSION_IDLE_TIMEOUT - elapsedSeconds;

      if (secondsLeft <= 0) {
        // Session expired
        setShowWarning(false);
        logout();
        nav("/login");
      } else if (secondsLeft <= WARNING_BEFORE) {
        // Show warning
        setShowWarning(true);
        setTimeLeft(secondsLeft);
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately, then periodically
    checkTimeout();
    timeoutCheckRef.current = setInterval(checkTimeout, CHECK_INTERVAL);

    return () => {
      if (timeoutCheckRef.current) {
        clearInterval(timeoutCheckRef.current);
      }
    };
  }, [user, logout, nav]);

  // "Continuar sesión" debe renovar también la sesión del servidor (cualquier request
  // autenticado la renueva); si ya expiró, el interceptor 401 manda a login.
  const renewSession = () => {
    recordActivity();
    auth.me().catch(() => {});
  };

  return {
    showWarning,
    timeLeft,
    renewSession,
  };
}

export default function SessionTimeoutWarning() {
  const { showWarning, timeLeft, renewSession } = useSessionTimeout();

  if (!showWarning) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div
      className="fixed bottom-4 right-4 max-w-sm bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 shadow-lg z-50"
      role="alert"
      data-testid="session-timeout-warning"
    >
      <h3 className="font-semibold text-yellow-900 mb-2">
        ⏱️ Sesión por expirar
      </h3>
      <p className="text-sm text-yellow-800 mb-3">
        Tu sesión se cerrará en {minutes}:{seconds.toString().padStart(2, "0")}{" "}
        minutos por inactividad.
      </p>
      <button
        onClick={renewSession}
        className="w-full px-3 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-medium text-sm"
        data-testid="session-timeout-renew-btn"
      >
        Continuar sesión
      </button>
    </div>
  );
}
